import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { Modal, Button } from 'react-bootstrap';
import { PartService } from '../../service/api/part-service';
import { POService } from '../../service/api/po-service';
import { flattenPartyOptions, partySelectGroups } from './vendor-customer-parties';

const UNIT_OPTIONS = ['PKG', 'CASE', 'EACH', 'BX', 'LBS', 'GAL', 'FT', 'KG', 'ROLL', 'M'];

function todayInputDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}
const selectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '38px',
    borderRadius: '8px',
    borderColor: '#d9d9d9',
    boxShadow: 'none',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 2000,
  }),
};

const partSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: '31px',
    borderRadius: '6px',
    borderColor: '#d9d9d9',
    boxShadow: 'none',
    fontSize: 13,
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 2000,
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: 13,
    zIndex: 2000,
  }),
};

const emptyShipTo = {
  id: null,
  shipToId: null,
  company: '',
  addr1: '',
  addr2: '',
  addr3: '',
  city: '',
  state: '',
  zip: '',
  country: '',
};

function shipToLabel(shipTo) {
  const name = shipTo.attn || shipTo.addr1 || 'Ship to';
  const place = [shipTo.city, shipTo.state].filter(Boolean).join(', ');
  return place ? `${name} — ${place}` : name;
}

const emptyLine = (seq) => ({
  localId: `local-line-${Date.now()}-${seq}`,
  seq,
  itemNo: '',
  description: '',
  quantityRequested: '',
  unit: 'EACH',
  reqDate: todayInputDate(),
  promiseDate: todayInputDate(),
  comment: '',
  receivedInThisShipment: 0,
  isVoided: false,
});

function Field({ label, column, children, className = 'col-md-4' }) {
  return (
    <div className={`${className} mb-2`}>
      <label className="form-label mb-0"><strong>{label}</strong></label>
      {column ? <span className="create-po-field-hint">{column}</span> : <span className="create-po-field-hint">&nbsp;</span>}
      {children}
    </div>
  );
}

const MappingsCreatePoModal = ({ show, onHide, onCreate, vendors = [], vendorsLoading = false }) => {
  const [vendorId, setVendorId] = useState(null);
  const [vendorName, setVendorName] = useState('');
  const [vendorNo, setVendorNo] = useState('');
  const [poDate, setPoDate] = useState(todayInputDate);
  const [rev, setRev] = useState('');
  const [buyer, setBuyer] = useState('');
  const [freightDesc, setFreightDesc] = useState('');
  const [onHold, setOnHold] = useState(false);
  const [reason, setReason] = useState('');
  const [lines, setLines] = useState([emptyLine(1)]);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [parts, setParts] = useState([]);
  const [partsLoading, setPartsLoading] = useState(false);
  const [shipTos, setShipTos] = useState([]);
  const [shipTosLoading, setShipTosLoading] = useState(false);
  const [shipTo, setShipTo] = useState(emptyShipTo);

  useEffect(() => {
    if (!show) return;
    const today = todayInputDate();
    setPoDate(today);
    setLines(prev => prev.map(line => ({
      ...line,
      reqDate: today,
      promiseDate: today,
    })));
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    let alive = true;
    setPartsLoading(true);
    PartService.getAllPartsList()
      .then((response) => {
        if (!alive) return;
        const list = response?.status === 200 || response?.response
          ? (response.response || [])
          : [];
        setParts(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setParts([]);
      })
      .finally(() => {
        if (alive) setPartsLoading(false);
      });
    return () => { alive = false; };
  }, [show]);

  useEffect(() => {
    if (!show) return undefined;
    let alive = true;
    setShipTosLoading(true);
    POService.getAllShipTos()
      .then((response) => {
        if (!alive) return;
        const list = response?.status === 200 || response?.response
          ? (response.response || [])
          : [];
        setShipTos(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setShipTos([]);
      })
      .finally(() => {
        if (alive) setShipTosLoading(false);
      });
    return () => { alive = false; };
  }, [show]);

  const partOptions = useMemo(
    () => (parts || [])
      .filter(part => part?.partNumber)
      .map(part => ({
        value: part.partNumber,
        label: part.description ? `${part.partNumber} — ${part.description}` : part.partNumber,
        description: part.description || '',
      })),
    [parts]
  );

  const shipToOptions = useMemo(
    () => (shipTos || []).map(item => ({
      value: item.id,
      label: shipToLabel(item),
      shipTo: item,
    })),
    [shipTos]
  );

  const vendorOptions = useMemo(
    () => partySelectGroups(vendors),
    [vendors]
  );
  const flatVendorOptions = useMemo(
    () => flattenPartyOptions(vendorOptions),
    [vendorOptions]
  );

  const resetForm = () => {
    setVendorId(null);
    setVendorName('');
    setVendorNo('');
    setPoDate(todayInputDate());
    setRev('');
    setBuyer('');
    setFreightDesc('');
    setOnHold(false);
    setReason('');
    setShipTo(emptyShipTo);
    setLines([emptyLine(1)]);
    setFormError('');
    setSaving(false);
  };

  const handleHide = () => {
    resetForm();
    onHide();
  };

  const updateLine = (localId, field, value) => {
    setLines(prev => prev.map(line => (
      line.localId === localId ? { ...line, [field]: value } : line
    )));
  };

  const selectLinePart = (localId, option) => {
    setLines(prev => prev.map(line => (
      line.localId === localId
        ? {
          ...line,
          itemNo: option ? option.value : '',
          description: option ? option.description : '',
        }
        : line
    )));
  };

  const handleAddLine = () => {
    setLines(prev => [...prev, emptyLine(prev.length + 1)]);
  };

  const handleRemoveLine = (localId) => {
    setLines(prev => prev
      .filter(line => line.localId !== localId)
      .map((line, index) => ({ ...line, seq: index + 1 })));
  };

  const handleCreate = async () => {
    if (!vendorId) {
      setFormError('Select a vendor or customer.');
      return;
    }
    const completedLines = lines.filter(line => line.itemNo.trim() || line.quantityRequested !== '');
    if (completedLines.length === 0 || completedLines.some(line => !line.itemNo.trim() || line.quantityRequested === '')) {
      setFormError('Each line needs an item number and a quantity.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      await onCreate({
        poNumber: '',
        isLocal: true,
        vendorId,
        vendorName,
        vendorNo: vendorNo.trim(),
        poDate,
        rev: rev.trim(),
        buyer: buyer.trim(),
        freightDesc,
        shipToId: shipTo.shipToId,
        shipToCompany: shipTo.company,
        shipToAddr1: shipTo.addr1,
        shipToAddr2: shipTo.addr2,
        shipToAddr3: shipTo.addr3,
        shipToCity: shipTo.city,
        shipToState: shipTo.state,
        shipToZip: shipTo.zip,
        shipToCountry: shipTo.country,
        onHold,
        reason: onHold ? reason.trim() : '',
        poIdExt: null,
        lines: completedLines.map((line, index) => ({
          ...line,
          seq: index + 1,
          itemNo: line.itemNo.trim(),
          description: line.description.trim(),
          quantityRequested: line.quantityRequested,
        })),
      });
      resetForm();
    } catch (error) {
      setFormError(error?.message || 'Failed to save the PO.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleHide} size="xl" centered scrollable enforceFocus={false}>
      <Modal.Header closeButton>
        <Modal.Title>Create Receipt</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {formError && <div className="alert alert-danger py-2">{formError}</div>}
        <div className="row">
          <Field label="Vendor/Customer" column="VendorID" className="col-md-6">
            <Select
              options={vendorOptions}
              value={flatVendorOptions.find(opt => opt.value === vendorId) || null}
              onChange={(option) => {
                setVendorId(option ? option.value : null);
                setVendorName(option ? option.label : '');
                setVendorNo(option?.vendorNo || '');
              }}
              isLoading={vendorsLoading}
              isClearable
              styles={selectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              placeholder="Select vendor or customer"
            />
          </Field>
          <Field label="Vendor/Customer no" column="vendorno" className="col-md-6">
            <input className="form-control" value={vendorNo} readOnly placeholder="From the selected vendor or customer" />
          </Field>
          <Field label="Ship to" column="ship_to_id" className="col-md-6">
            <Select
              options={shipToOptions}
              value={shipToOptions.find(opt => opt.value === shipTo.id) || null}
              onChange={(option) => {
                const selected = option?.shipTo;
                setShipTo(selected ? {
                  id: selected.id,
                  shipToId: selected.shipToIdExt ?? selected.id ?? null,
                  company: selected.attn || '',
                  addr1: selected.addr1 || '',
                  addr2: selected.addr2 || '',
                  addr3: selected.addr3 || '',
                  city: selected.city || '',
                  state: selected.state || '',
                  zip: selected.zip || '',
                  country: selected.country || '',
                } : emptyShipTo);
              }}
              isLoading={shipTosLoading}
              isClearable
              styles={selectStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
              placeholder="Select ship to"
            />
          </Field>
          <Field label="Ship to company" column="ship_to_company" className="col-md-6">
            <input className="form-control" value={shipTo.company} readOnly />
          </Field>
          <Field label="Address 1" column="ship_to_addr1" className="col-md-6">
            <input className="form-control" value={shipTo.addr1} readOnly />
          </Field>
          <Field label="Address 2" column="ship_to_addr2" className="col-md-6">
            <input className="form-control" value={shipTo.addr2} readOnly />
          </Field>
          <Field label="Address 3" column="ship_to_addr2" className="col-md-4">
            <input className="form-control" value={shipTo.addr3} readOnly />
          </Field>
          <Field label="City" column="ship_to_city" className="col-md-4">
            <input className="form-control" value={shipTo.city} readOnly />
          </Field>
          <Field label="State" column="ship_to_state" className="col-md-4">
            <input className="form-control" value={shipTo.state} readOnly />
          </Field>
          <Field label="Zip" column="ship_to_zip" className="col-md-4">
            <input className="form-control" value={shipTo.zip} readOnly />
          </Field>
          <Field label="Country" column="ship_to_country" className="col-md-4">
            <input className="form-control" value={shipTo.country} readOnly />
          </Field>
          <Field label="PO date" column="po_date" className="col-md-3">
            <input type="date" className="form-control" value={poDate} onChange={e => setPoDate(e.target.value)} />
          </Field>
          <Field label="Rev" column="rev" className="col-md-3">
            <input className="form-control" value={rev} onChange={e => setRev(e.target.value)} />
          </Field>
          <Field label="Buyer" column="buyerid" className="col-md-3">
            <input className="form-control" value={buyer} onChange={e => setBuyer(e.target.value)} />
          </Field>
          <Field label="Freight" column="freight_desc" className="col-md-3">
            <input className="form-control" value={freightDesc} onChange={e => setFreightDesc(e.target.value)} />
          </Field>
          <div className="col-md-4 mb-2">
            <div className="form-check mt-4">
              <input
                className="form-check-input"
                type="checkbox"
                id="create-po-on-hold"
                checked={onHold}
                onChange={e => setOnHold(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="create-po-on-hold">
                On hold <span className="create-po-field-hint d-inline">on_hold</span>
              </label>
            </div>
          </div>
          {onHold && (
            <Field label="Reason" column="reason" className="col-md-8">
              <input className="form-control" value={reason} onChange={e => setReason(e.target.value)} />
            </Field>
          )}
        </div>
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="mb-0">Lines</h6>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleAddLine}>Add line</button>
        </div>
        <div className="table-responsive create-po-lines-wrap">
          <table className="table table-sm align-middle create-po-lines-table mb-0">
            <thead>
              <tr>
                <th>Seq<span>SEQ</span></th>
                <th>Item no<span>MISC_ITEMNO</span></th>
                <th>Description<span>MISC_ITEM</span></th>
                <th>Qty<span>QuantityRequested</span></th>
                <th>Unit<span>UNIT</span></th>
                <th>Req date<span>REQ_DATE</span></th>
                <th>Promise<span>PROMISE_DATE</span></th>
                <th>Comment<span>cmtline</span></th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map(line => (
                <tr key={line.localId}>
                  <td className="text-muted">{line.seq}</td>
                  <td style={{ minWidth: 240 }}>
                    <Select
                      options={partOptions}
                      value={partOptions.find(opt => opt.value === line.itemNo) || null}
                      onChange={option => selectLinePart(line.localId, option)}
                      isLoading={partsLoading}
                      isClearable
                      styles={partSelectStyles}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition="fixed"
                      placeholder="Select part"
                    />
                  </td>
                  <td>
                    <input className="form-control form-control-sm" value={line.description} onChange={e => updateLine(line.localId, 'description', e.target.value)} />
                  </td>
                  <td>
                    <input type="number" min="0" className="form-control form-control-sm" value={line.quantityRequested} onChange={e => updateLine(line.localId, 'quantityRequested', e.target.value)} />
                  </td>
                  <td>
                    <select className="form-select form-select-sm" value={line.unit} onChange={e => updateLine(line.localId, 'unit', e.target.value)}>
                      {UNIT_OPTIONS.map(unit => (
                        <option key={unit} value={unit}>{unit}</option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input type="date" className="form-control form-control-sm" value={line.reqDate} onChange={e => updateLine(line.localId, 'reqDate', e.target.value)} />
                  </td>
                  <td>
                    <input type="date" className="form-control form-control-sm" value={line.promiseDate} onChange={e => updateLine(line.localId, 'promiseDate', e.target.value)} />
                  </td>
                  <td>
                    <input className="form-control form-control-sm" value={line.comment} onChange={e => updateLine(line.localId, 'comment', e.target.value)} />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleRemoveLine(line.localId)}
                      disabled={lines.length === 1}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleHide} disabled={saving}>Cancel</Button>
        <Button variant="warning" onClick={handleCreate} disabled={saving}>
          {saving ? 'Saving...' : 'Create Receipt'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MappingsCreatePoModal;
